<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }"
            data-cy="podFiles-tree-actions-renameAsset-activator"
            name="activator" />
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span>{{ $t('routes.pod.renameAssetDialogTitle') }}</span>
        <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-form ref="form" v-on:submit.prevent="formSubmit()" data-cy="dialog-renameAsset-modal">
        <v-card-text class="pb-0">
          {{ $t('routes.pod.renameAssetDialogFilename') }}:
          <v-text-field
            v-model="filenameBase"
            v-bind:rules="filenameRules"
            v-bind:placeholder="$t('routes.pod.renameAssetDialogFilename')"
            :maxlength="filenameBaseMaxLength"
            variant="solo-filled"
            required
            block
            flat
            data-cy="dialog-renameAsset-assetName"
          >
            <template v-if="filenameExtension" v-slot:append-inner>
              <span class="filename-extension text-medium-emphasis" aria-hidden="true">
                {{ filenameExtension }}
              </span>
            </template>
          </v-text-field>

          <div v-if="errorMessage" class="bg-error" data-cy="dialog-renameAsset-error">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" type="submit" variant="flat" data-cy="dialog-renameAsset-ok">
            {{ $t('common.rename') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" data-cy="dialog-renameAsset-false">
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import FilenameHelper from '@/components/utils/FilenameHelper.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "RenameAssetDialog",
  props: {
    pod: {
      type: Object,
      required: true,
    },
    asset: {
      type: Object,
      required: false
    }
  },
  data: function() {
    return {
      filenameBase: '',
      filenameExtension: '',
      filenameRules: [
        v => !!v || this.$t('routes.pod.renameAssetDialogInvalidName'),
        v => v && v.length <= this.filenameBaseMaxLength || this.$t('routes.pod.fileNameTooLong'),
      ],
      errorMessage: "",
    };
  },
  watch: {
    dialog: function(newValue) {
      if (newValue) {
        this.setFilenameParts(this.asset?.name || '');
        this.errorMessage = "";
      }
    }
  },
  computed: {
    filenameBaseMaxLength() {
      return Math.max(0, 191 - this.filenameExtension.length);
    },
  },
  methods: {
    setFilenameParts(filename) {
      const parts = FilenameHelper.splitFilename(filename);

      this.filenameBase = parts.baseName;
      this.filenameExtension = parts.extension;
    },
    formSubmit: async function() {
      const validForm = await this.$refs.form.validate();
      this.errorMessage = "";
      if (!validForm.valid) {
        return;
      }

      let data = {
        podID: this.pod.id,
        assetID: this.asset.id,
        payload: {
          name: this.filenameBase,
        }
      };

      let self = this;
      const podStore = usePodStore();
      podStore.updatePodAsset(data)
      .then(function() {
        self.dialog = false;
      })
      .catch(function(error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          self.errorMessage = error.response.data.error.message;
        } else {
          self.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      });
    },
  }
}
</script>

<style lang="scss" scoped>
.filename-extension {
  white-space: nowrap;
  pointer-events: none;
}
</style>
